import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";
import PatientIdBadge from "../components/PatientIdBadge";
import Cognimeter, { CognimeterScale, CognimeterWording, TEAL } from "../components/Cognimeter";
import CognimeterLegend, { CognimeterLegendLayout } from "../components/CognimeterLegend";
import CognitiveReport from "../components/CognitiveReport";
import { getScanResults, rescoreMci, ScanResults } from "../api/scans";
import { formatLocalDateTime } from "../utils/datetime";
import { DEFAULT_DISCLAIMER, DEFAULT_DISCLAIMER_TITLE } from "../utils/mci";

// Physician (production, English UI): результат теста = когниметр («Cognitive
// Function Scale»), «Tap for full report» раскрывает когнитивный профиль
// (радар) и драйверы оценки из `more_info` (CognitiveReport).
//
// Экран повторяет референс-отчёт CogNimbus_v2/more_info_KZ2-66M673.html:
// шкала + легенда + «Tap for full report» → профиль и драйверы. Всё, чего в
// референсе нет, юзер попросил убрать (27.07.2026): OOD-предупреждение,
// подпись внутренней зоны модели и блок «Test data»
// (аудио/транскрипции/кластеры/MST). Non-diagnostic labeling follows the
// FDA wording selected for Balanced design v1 in Sept 2026.

const ROLE_LABELS: Record<string, string> = {
  physician: "physician",
  test_administrator: "nurse",
  assistant: "assistant",
};

export default function PhysicianScanPage() {
  const { scanId } = useParams<{ patientId: string; scanId: string }>();
  const [results, setResults] = useState<ScanResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [rescoring, setRescoring] = useState(false);
  // Варианты когниметра для обсуждения с FDA-экспертом (сент. 2026): шкала и
  // формулировки переключаются табами над шкалой, дефолт — Balanced design v2.
  // Один утверждённый вид результата на релиз: выбора шкалы и формулировок на странице нет.
  const scale: CognimeterScale = "reference";
  const wording: CognimeterWording = "current";
  const legendLayout = "side" as CognimeterLegendLayout;
  const reportRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    if (!scanId) return;
    try {
      const data = await getScanResults(scanId);
      setResults(data);
      setError("");
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to load test results");
    } finally {
      setLoading(false);
    }
  }, [scanId]);

  useEffect(() => {
    load();
  }, [load]);

  // Пока идёт обработка блоков или анализ модели — обновляем раз в 5 секунд
  const inProgress =
    results != null &&
    (Object.values(results.blocks).some((b) => b?.processing_status === "processing") ||
      results.mci?.status === "processing" ||
      (results.mci?.status == null && results.mci?.enabled && !results.mci?.inputs_ready));

  useEffect(() => {
    if (!inProgress) return;
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [inProgress, load]);

  const toggleFullReport = () => {
    setReportOpen((open) => {
      if (!open) {
        setTimeout(() => reportRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
      }
      return !open;
    });
  };

  const handleRescore = async () => {
    if (!scanId) return;
    setRescoring(true);
    try {
      await rescoreMci(scanId);
      await load();
    } finally {
      setRescoring(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !results) {
    return <Alert severity="error">{error || "Results not found"}</Alert>;
  }

  const { scan, mci } = results;
  const operator = scan.operator_name || scan.operator_email || "—";
  const roleLabel = scan.operator_role ? ROLE_LABELS[scan.operator_role] : null;
  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1 }}>Test result</Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
        {scan.patient_id && <PatientIdBadge patientId={scan.patient_id} />}
        <Typography variant="body2" color="text.secondary">
          {formatLocalDateTime(scan.scan_start || scan.created_at)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Conducted by: {operator}{roleLabel ? ` (${roleLabel})` : ""}
        </Typography>
        {scan.language && (
          <Chip label={scan.language.toUpperCase()} size="small" variant="outlined" />
        )}
      </Box>

      {scan.attempt_status === "interrupted" && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Incomplete attempt — the test was interrupted. Recorded blocks are shown below; this attempt is not scored.
        </Alert>
      )}
      {scan.administration === "remote" && scan.attempt_status !== "interrupted" && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Unsupervised remote administration — the model was validated on supervised iPad sessions.
        </Alert>
      )}

      {/* ==== Когниметр ==== */}
      {scan.attempt_status !== "interrupted" && (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        {!mci?.enabled ? (
          <Alert severity="info">
            The analysis model is not connected — contact your administrator.
          </Alert>
        ) : mci.status === "done" && mci.result ? (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", containerType: "inline-size", containerName: "cognimeter" }}>
              <Box sx={{ flex: "1 1 420px", minWidth: 0 }}>
                <Cognimeter
                  confidence={mci.result.confidence_pct}
                  thresholds={mci.result.thresholds}
                  scale={scale}
                  wording={wording}
                />
              </Box>
              {legendLayout === "side" && <CognimeterLegend scale={scale} layout="side" />}
            </Box>
            <Box
              component="ul"
              sx={{
                mt: 2, mb: 0, py: 1.8, px: 3, pl: 5,
                bgcolor: "#f5f8fc", border: "1px solid #dbe3ee", borderRadius: 2,
                color: "#26344a", fontSize: 17, lineHeight: 1.6,
              }}
            >
              <li>The arrow represents the cognitive function score measured by the test.</li>
              <li>
                The <b style={{ color: "#5b6776" }}>gray zone</b> covers scores 33–46;
                its score boundaries are 32 and 47.
              </li>
            </Box>
            {legendLayout === "below" && <CognimeterLegend scale={scale} />}
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              <Alert severity="info" sx={{ alignItems: "flex-start" }}>
                <Typography component="div" sx={{ fontWeight: 700, mb: 0.35 }}>
                  {DEFAULT_DISCLAIMER_TITLE}
                </Typography>
                {DEFAULT_DISCLAIMER}
              </Alert>
              {mci.result.mst_compliance?.flag && (
                <Alert severity="warning">
                  The memory block (task 4) was likely answered at random — memory
                  metrics are unreliable.
                </Alert>
              )}
              {(mci.result.n_features_missing || 0) > 3 && (
                <Alert severity="info">
                  Some test data is missing ({mci.result.n_features_missing} features) —
                  session quality is reduced, consider retesting.
                </Alert>
              )}
            </Stack>
          </>
        ) : mci.status === "failed" ? (
          <Alert
            severity="error"
            action={
              <Button
                size="small"
                startIcon={rescoring ? <CircularProgress size={14} /> : <ReplayIcon />}
                onClick={handleRescore}
                disabled={rescoring}
              >
                Rescore
              </Button>
            }
          >
            Failed to compute the result. Try rescoring.
          </Alert>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2, justifyContent: "center" }}>
            <CircularProgress size={22} />
            <Typography variant="body2" color="text.secondary">
              {mci.status === "processing"
                ? "Analyzing results..."
                : "Waiting for test data (audio processing)..."}
            </Typography>
          </Box>
        )}
      </Paper>
      )}

      {/* ==== Tap for full report ==== */}
      {mci?.status === "done" && (
        <Button
          onClick={toggleFullReport}
          sx={{
            display: "block", width: "calc(100% - 32px)", mx: 2, mb: 2, py: 1.6,
            border: `1.5px solid ${TEAL}`, borderRadius: 3, bgcolor: "#fff",
            color: TEAL, fontSize: 17, fontWeight: 700, textTransform: "none",
            "&:hover": { bgcolor: "#F5F8F8" },
          }}
        >
          {reportOpen ? "Hide full report ▴" : "Tap for full report ▾"}
        </Button>
      )}

      <Collapse in={reportOpen}>
        <Box ref={reportRef}>
          {mci?.result?.more_info && <CognitiveReport moreInfo={mci.result.more_info} />}
        </Box>
      </Collapse>
    </Box>
  );
}
